import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from '../components/CustomToast';
import {
  X, Plus, Trash2, Loader2, Save, Search, User, Brain,
  Languages, Heart, Mic, Shield, GripVertical, AlertTriangle, Info
} from 'lucide-react';

function FieldLabel({ text, tooltip, required }) {
  return (
    <label className="flex items-center gap-1.5 mb-1 text-xs font-semibold text-slate-600">
      {text} {required && <span className="text-red-400">*</span>}
      {tooltip && (
        <div className="group relative flex items-center">
          <Info className="w-3 h-3 text-slate-400 cursor-help" />
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block w-48 p-2 bg-slate-800 text-slate-100 text-[11px] leading-tight font-normal rounded shadow-lg z-50 pointer-events-none text-center">
            {tooltip}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-slate-800"></div>
          </div>
        </div>
      )}
    </label>
  );
}

// ── ICD-10 Typeahead Hook ──
function useIcdSearch() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const search = useCallback((query) => {
    clearTimeout(timerRef.current);
    if (!query || query.length < 2) { setResults([]); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search?sf=code,name&terms=${encodeURIComponent(query)}&maxList=10`
        );
        const data = await res.json();
        setResults((data[3] || []).map((e) => ({ code: e[0], label: e[1] })));
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
  }, []);
  const clear = useCallback(() => { setResults([]); setLoading(false); }, []);
  return { results, loading, search, clear };
}

// ── Chip Input Component ──
function ChipInput({ chips, onChange, placeholder, variant = 'default' }) {
  const [input, setInput] = useState('');
  const inputRef = useRef(null);

  const addChip = (text) => {
    const trimmed = text.trim();
    if (trimmed && !chips.includes(trimmed)) onChange([...chips, trimmed]);
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault();
      addChip(input);
      setInput('');
    }
    if (e.key === 'Backspace' && !input && chips.length > 0) {
      onChange(chips.slice(0, -1));
    }
  };

  return (
    <div className="pf-chip-input-wrapper" onClick={() => inputRef.current?.focus()}>
      {chips.map((chip, i) => (
        <span key={i} className={`pf-chip ${variant === 'danger' ? 'danger' : ''}`}>
          {chip}
          <button type="button" onClick={() => onChange(chips.filter((_, j) => j !== i))}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (input.trim()) { addChip(input); setInput(''); } }}
        placeholder={chips.length === 0 ? placeholder : ''}
        className="pf-chip-text-input"
      />
    </div>
  );
}

// ── Metaprompt Template ──
function compileMetaprompt(d) {
  const timeline = (d.symptom_timeline || []).map((s, i) => `${i + 1}. ${s}`).join('\n');
  const hidden = (d.hidden_history || []).join(', ');

  return `You are an advanced AI actor simulating a real patient for telemedicine training.

CRITICAL BASELINE PERSONA:
- Name: ${d.name} | Age: ${d.age} | Gender: ${d.gender}
- Language Constraint: You must speak entirely in ${d.language}. If set to "hindi" or "hinglish", you must output all Hindi text strictly using Devanagari script. You are strictly forbidden from outputting romanized/transliterated Hindi text.
- Vocabulary Style: Your medical literacy is ${d.literacy_level}. Adjust your terms accordingly.
- Personality: Act out the conversation using a ${d.mood_profile} demeanor.

CONVERSATION & DISCLOSURE RULES:
1. Turn 1 Rule: You must open the conversation by saying exactly: "${d.chief_complaint}". Do not say anything else first.
2. Progressive Disclosure: Throughout the session, progressively reveal items from this list as the conversation naturally goes deeper:
${timeline}
3. Gatekeeping Rule: You possess critical background facts: ${hidden}. You are strictly forbidden from disclosing these facts unless the doctor explicitly asks you about them. If they do not ask, take this secret to the end of the call.

BEHAVIORAL RULES:
- Never break character. You are ${d.name}, not an AI.
- Never provide medical advice. You are the patient, not the doctor.
- React naturally to questions. If confused by medical jargon (due to low literacy), ask for clarification.
- If the doctor is rude or dismissive, react according to your ${d.mood_profile} personality.`;
}

// ── Empty Rx Row ──
const emptyRxRow = () => ({ id: crypto.randomUUID(), drug: '', min_dose: '', max_dose: '', frequencies: '' });

// ── Rubric Presets ──
const RUBRIC_PRESETS = [
  { id: 'general', label: 'General Consultation Rubric', weights: { history_taking: 20, diagnosis: 20, rx_accuracy: 15, investigations: 15, communication: 15, red_flag_handling: 15 } },
  { id: 'emergency', label: 'Emergency Triage Rubric', weights: { history_taking: 15, diagnosis: 15, rx_accuracy: 10, investigations: 10, communication: 20, red_flag_handling: 30 } },
  { id: 'pediatric', label: 'Pediatric Consultation Rubric', weights: { history_taking: 25, diagnosis: 20, rx_accuracy: 15, investigations: 15, communication: 15, red_flag_handling: 10 } },
];

// ═════════════════════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════════════════════
export default function PatientPersonaForm({ accountId, accountName, existingCase, onClose, onSaved }) {
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const icdSearch = useIcdSearch();
  const icdRef = useRef(null);

  // ── Form State ──
  const [name, setName] = useState(existingCase?.patient?.name || '');
  const [age, setAge] = useState(existingCase?.patient?.age || '');
  const [gender, setGender] = useState(existingCase?.patient?.gender || 'male');
  const [language, setLanguage] = useState(existingCase?.patient?.language || 'hinglish');
  const [literacyLevel, setLiteracyLevel] = useState(existingCase?.literacy_level || 'medium');
  const [moodProfile, setMoodProfile] = useState(existingCase?.patient?.mood || 'cooperative');
  const [chiefComplaint, setChiefComplaint] = useState(existingCase?.chief_complaint || '');
  const [symptomTimeline, setSymptomTimeline] = useState(existingCase?.symptom_timeline || ['']);
  const [hiddenHistory, setHiddenHistory] = useState(existingCase?.hidden_history || []);
  const [difficulty, setDifficulty] = useState(existingCase?.difficulty || 'Beginner');
  const [category, setCategory] = useState(existingCase?.category || '');
  const [isEmergency, setIsEmergency] = useState(existingCase?.is_emergency || false);
  const [voiceId, setVoiceId] = useState(existingCase?.voice_id || '');
  const [maxAttempts, setMaxAttempts] = useState(existingCase?.max_attempts || 5);
  const [voices, setVoices] = useState([]);

  // Load voices for admin
  useEffect(() => {
    (async () => {
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${apiUrl}/api/v1/admin/voices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVoices(res.data?.voices || []);
        if (!existingCase?.voice_id && res.data?.voices?.length > 0) {
          const firstVoice = res.data.voices[0];
          setVoiceId(firstVoice.provider_voice || firstVoice.local_voice_name || firstVoice.id);
        }
      } catch (err) {
        console.error('Failed to load voices', err);
      }
    })();
  }, [token, existingCase]);

  // Diagnosis
  const [expectedDx, setExpectedDx] = useState(
    existingCase?.expected_diagnosis ? existingCase.expected_diagnosis : null
  );
  const [dxQuery, setDxQuery] = useState('');

  // Acceptable Rx
  const [acceptableRx, setAcceptableRx] = useState(
    existingCase?.acceptable_rx?.length
      ? existingCase.acceptable_rx.map((r) => ({ ...r, id: crypto.randomUUID() }))
      : [emptyRxRow()]
  );

  // Forbidden Rx
  const [forbiddenRx, setForbiddenRx] = useState(existingCase?.forbidden_rx || []);

  // Rubric
  const [rubricId, setRubricId] = useState(existingCase?.scoring_rubric_id || 'general');

  // Metaprompt
  const [manualPrompt, setManualPrompt] = useState(existingCase?.prompt?.system_prompt || '');
  const [isCustomPrompt, setIsCustomPrompt] = useState(false);

  // ── Symptom Timeline helpers ──
  const addTimelineItem = () => setSymptomTimeline((prev) => [...prev, '']);
  const updateTimelineItem = (idx, val) =>
    setSymptomTimeline((prev) => prev.map((s, i) => (i === idx ? val : s)));
  const removeTimelineItem = (idx) =>
    setSymptomTimeline((prev) => prev.filter((_, i) => i !== idx));

  // ── Rx helpers ──
  const addRxRow = () => setAcceptableRx((prev) => [...prev, emptyRxRow()]);
  const removeRxRow = (id) => setAcceptableRx((prev) => prev.filter((r) => r.id !== id));
  const updateRxRow = (id, field, value) =>
    setAcceptableRx((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  // ── Generate initials ──
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // ── Save ──
  const handleSave = async (override = false) => {
    if (!name.trim()) { toast.error('Patient name is required'); return; }
    if (!chiefComplaint.trim()) { toast.error('Chief complaint is required'); return; }
    if (!expectedDx) { toast.error('Expected diagnosis is required'); return; }

    const personaData = {
      name: name.trim(),
      age: Number(age) || 0,
      gender,
      language,
      literacy_level: literacyLevel,
      mood_profile: moodProfile,
      chief_complaint: chiefComplaint.trim(),
      symptom_timeline: symptomTimeline.filter((s) => s.trim()),
      hidden_history: hiddenHistory,
    };

    const finalPrompt = isCustomPrompt ? manualPrompt : compileMetaprompt(personaData);
    const rubricPreset = RUBRIC_PRESETS.find((r) => r.id === rubricId) || RUBRIC_PRESETS[0];

    try {
      setSaving(true);
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      // Create the case document
      const casePayload = {
        title: `${name.trim()} — ${category || 'General Consultation'}`,
        client_id: accountId,
        difficulty,
        category: category || 'General',
        is_emergency: isEmergency,
        patient: {
          name: name.trim(),
          initials: initials || 'PT',
          age: Number(age) || 0,
          gender,
          language,
          mood: moodProfile,
        },
        chief_complaint: chiefComplaint.trim(),
        voice_id: voiceId,
        voice_name: (voices.find(v => (v.provider_voice || v.local_voice_name || v.id) === voiceId) || {}).local_voice_name || voiceId,
        prompt: {
          system_prompt: finalPrompt,
          first_message: chiefComplaint.trim(),
        },
        max_duration_sec: 600,
        max_attempts: Number(maxAttempts) || 5,
        rubric: rubricPreset.weights,
        // Extended fields for scoring engine
        expected_diagnosis: expectedDx,
        acceptable_rx: acceptableRx
          .filter((r) => r.drug.trim())
          .map(({ id, ...rest }) => rest),
        forbidden_rx: forbiddenRx,
        hidden_history: hiddenHistory,
        symptom_timeline: symptomTimeline.filter((s) => s.trim()),
        literacy_level: literacyLevel,
        scoring_rubric_id: rubricId,
      };

      if (override && existingCase) {
        await axios.put(`${apiUrl}/api/v1/clinicalsim/cases/${existingCase.case_id}`, casePayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(`Patient "${name.trim()}" updated successfully`);
      } else {
        await axios.post(`${apiUrl}/api/v1/clinicalsim/cases`, casePayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(`Patient "${name.trim()}" created successfully`);
      }

      onSaved();
    } catch (err) {
      console.error('Save failed', err);
      toast.error(err.response?.data?.detail || 'Failed to save patient persona');
    } finally {
      setSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="pf-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pf-panel">
        {/* Header */}
        <div className="pf-panel-header">
          <div>
            <h2>{existingCase ? 'Edit Patient Persona' : 'Create Patient Persona'}</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Assigning to: <span className="font-semibold text-slate-600">{accountName}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="pf-panel-body">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-6">
            
            {/* ── LEFT COLUMN ── */}
            <div className="flex flex-col gap-6">
              {/* ═══ A. Basic Info ═══ */}
          <div className="pf-section flex flex-col gap-4">
            <div className="pf-section-title !mb-0">
              <User className="w-4 h-4 text-purple-500" /> Basic Info
            </div>

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-8 pf-field !mb-0">
                <FieldLabel text="Patient Name" required tooltip="The identity the AI actor will assume." />
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rajesh Kumar" />
              </div>
              <div className="col-span-4 pf-field !mb-0">
                <FieldLabel text="Age" required tooltip="The biological age of the patient in years." />
                <input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="45" min="0" max="120" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="pf-field !mb-0">
                <FieldLabel text="Gender" tooltip="Patient gender for the AI to simulate." />
                <select value={gender} onChange={(e) => setGender(e.target.value)}>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div className="pf-field !mb-0">
                <FieldLabel text="Language" tooltip="The strict language constraint the AI must abide by (e.g., must speak Hinglish)." />
                <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                  <option value="hindi">Hindi</option>
                  <option value="english">English</option>
                  <option value="hinglish">Hinglish</option>
                </select>
              </div>
              <div className="pf-field !mb-0">
                <FieldLabel text="Literacy Level" tooltip="Dictates how complex the AI's medical vocabulary will be." />
                <select value={literacyLevel} onChange={(e) => setLiteracyLevel(e.target.value)}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-4 pf-field !mb-0">
                <FieldLabel text="Mood Profile" tooltip="The emotional state of the patient (e.g. Anxious patients will interrupt more)." />
                <select value={moodProfile} onChange={(e) => setMoodProfile(e.target.value)}>
                  <option value="cooperative">Cooperative</option>
                  <option value="anxious">Anxious</option>
                  <option value="dismissive">Dismissive</option>
                  <option value="verbose">Verbose</option>
                </select>
              </div>
              <div className="col-span-3 pf-field !mb-0">
                <FieldLabel text="Difficulty" tooltip="The clinical difficulty rating for this scenario." />
                <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
              <div className="col-span-3 pf-field !mb-0">
                <FieldLabel text="Category" tooltip="Medical specialty this case falls under." />
                <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Cardiology" />
              </div>
              <div className="col-span-2 pf-field !mb-0">
                <FieldLabel text="Max Attempts" tooltip="Limit how many times the doctor can attempt this case." />
                <input type="number" value={maxAttempts} onChange={(e) => setMaxAttempts(e.target.value)} placeholder="5" min="1" max="100" />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="pf-field !mb-0">
                <FieldLabel text="Voice" tooltip="The underlying TTS model used to synthesize speech." />
                <select value={voiceId} onChange={(e) => setVoiceId(e.target.value)}>
                  {voices.map(v => (
                    <option key={v.id} value={v.provider_voice || v.local_voice_name || v.id}>
                      {v.local_voice_name || v.id} ({v.provider})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="pf-field" style={{ marginTop: 4 }}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isEmergency} onChange={(e) => setIsEmergency(e.target.checked)} className="accent-red-500" />
                <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                <span className="text-xs font-medium text-slate-600">Emergency / Red-flag case</span>
              </label>
            </div>
          </div>

          {/* ═══ B. Symptom & Presentation ═══ */}
          <div className="pf-section">
            <div className="pf-section-title">
              <Mic className="w-4 h-4 text-emerald-500" /> Symptom &amp; Presentation
            </div>

            <div className="pf-field">
              <FieldLabel text="Chief Complaint (Opening Line)" required tooltip="The absolute first thing the AI will say to start the session." />
              <textarea
                value={chiefComplaint}
                onChange={(e) => setChiefComplaint(e.target.value)}
                placeholder="The exact opening line the patient says when the call starts…"
                rows={2}
              />
            </div>

            <div className="pf-field">
              <FieldLabel text="Symptom Timeline (Progressive Disclosure)" tooltip="Symptoms the AI should reveal sequentially as the doctor asks more probing questions." />
              <div className="pf-timeline-list">
                {symptomTimeline.map((item, idx) => (
                  <div key={idx} className="pf-timeline-card">
                    <span className="pf-timeline-num">{idx + 1}</span>
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => updateTimelineItem(idx, e.target.value)}
                      placeholder={`Symptom or detail #${idx + 1}…`}
                    />
                    {symptomTimeline.length > 1 && (
                      <button type="button" className="pf-timeline-remove" onClick={() => removeTimelineItem(idx)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" className="pf-add-row-btn" onClick={addTimelineItem}>
                <Plus className="w-3.5 h-3.5" /> Add Symptom
              </button>
            </div>
          </div>

          {/* ═══ C. Hidden History ═══ */}
          <div className="pf-section">
            <div className="pf-section-title">
              <Shield className="w-4 h-4 text-amber-500" /> Hidden History (Probing Guardrails)
            </div>
            <div className="pf-field">
              <FieldLabel text="Hidden facts (press Enter to add)" tooltip="Secrets the AI will ONLY disclose if specifically asked about by the doctor." />
              <ChipInput
                chips={hiddenHistory}
                onChange={setHiddenHistory}
                placeholder="e.g. smoking history, family CAD, drug allergy…"
              />
              <p className="text-xs text-slate-400 mt-1">
                Agent won't reveal these unless the doctor explicitly asks.
              </p>
            </div>
          </div>

          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="flex flex-col gap-6">
            {/* ═══ D. System Prompt Configuration ═══ */}
            <div className="pf-section m-0">
            <div className="pf-section-title flex items-center justify-between">
              <span className="flex items-center gap-1.5"><Brain className="w-4 h-4 text-indigo-500" /> System Prompt Configuration</span>
              <label className="flex items-center gap-2 text-xs font-normal cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={isCustomPrompt} 
                  onChange={(e) => {
                    setIsCustomPrompt(e.target.checked);
                    if (e.target.checked && !manualPrompt) {
                      const pd = {
                        name: name.trim(), age: Number(age) || 0, gender, language,
                        literacy_level: literacyLevel, mood_profile: moodProfile,
                        chief_complaint: chiefComplaint.trim(),
                        symptom_timeline: symptomTimeline.filter((s) => s.trim()),
                        hidden_history: hiddenHistory,
                      };
                      setManualPrompt(compileMetaprompt(pd));
                    }
                  }} 
                />
                Customize Prompt Manually
              </label>
            </div>
            {isCustomPrompt ? (
              <div className="pf-field">
                <FieldLabel text="Custom System Prompt" tooltip="The raw system instructions sent to the AI. Modify with caution." />
                <textarea
                  value={manualPrompt}
                  onChange={(e) => setManualPrompt(e.target.value)}
                  rows={12}
                  className="w-full p-3 font-mono text-xs bg-slate-900 text-slate-100 rounded-xl"
                  placeholder="You are an advanced AI actor..."
                />
              </div>
            ) : (
              <div className="text-xs text-slate-500 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <p className="mb-2 font-semibold">The prompt is auto-generated using the fields above.</p>
                <p>To edit the core AI instructions, structural rules, or behavioral constraints, check the "Customize Prompt Manually" box.</p>
              </div>
            )}
          </div>

          {/* ═══ E. Medical Validation ═══ */}
          <div className="pf-section">
            <div className="pf-section-title">
              <Shield className="w-4 h-4 text-blue-500" /> Medical Validation &amp; Guardrails
            </div>

            {/* Expected Diagnosis — ICD Typeahead */}
            <div className="pf-field">
              <FieldLabel text="Expected Diagnosis (ICD-10)" required tooltip="The correct diagnosis the doctor should reach by the end of the scenario. Used for scorecard evaluation." />
              {expectedDx ? (
                <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <span className="px-2 py-0.5 bg-white rounded font-mono text-xs font-bold text-emerald-600 border border-emerald-100">{expectedDx.code}</span>
                  <span className="text-sm text-emerald-800 flex-1">{expectedDx.label}</span>
                  <button type="button" onClick={() => setExpectedDx(null)} className="text-emerald-400 hover:text-red-400">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="relative" ref={icdRef}>
                  <input
                    type="text"
                    value={dxQuery}
                    onChange={(e) => { setDxQuery(e.target.value); icdSearch.search(e.target.value); }}
                    placeholder="Search ICD-10 codes…"
                    autoComplete="off"
                  />
                  {(icdSearch.results.length > 0 || icdSearch.loading) && (
                    <div className="icd-dropdown" style={{ position: 'absolute', zIndex: 60, top: 'calc(100% + 4px)', left: 0, right: 0 }}>
                      {icdSearch.loading ? (
                        <div className="icd-dropdown-loading"><Loader2 className="w-4 h-4 animate-spin" /> Searching…</div>
                      ) : icdSearch.results.map((item, idx) => (
                        <div key={idx} className="icd-dropdown-item" onClick={() => { setExpectedDx(item); setDxQuery(''); icdSearch.clear(); }}>
                          <span className="icd-code">{item.code}</span>
                          <span className="icd-label">{item.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Acceptable Rx Grid */}
            <div className="pf-field mt-6">
              <FieldLabel text="Acceptable Rx Range" tooltip="Medications that are considered correct treatments for this case. The scorecard checks if the doctor prescribes within this range." />
              <div className="pf-rx-grid">
                <div className="pf-rx-head">
                  <span>Drug Name</span>
                  <span>Min Dose</span>
                  <span>Max Dose</span>
                  <span>Allowed Frequencies</span>
                  <span></span>
                </div>
                {acceptableRx.map((row) => (
                  <div key={row.id} className="pf-rx-row">
                    <input type="text" value={row.drug} onChange={(e) => updateRxRow(row.id, 'drug', e.target.value)} placeholder="Drug" />
                    <input type="text" value={row.min_dose} onChange={(e) => updateRxRow(row.id, 'min_dose', e.target.value)} placeholder="e.g. 250mg" />
                    <input type="text" value={row.max_dose} onChange={(e) => updateRxRow(row.id, 'max_dose', e.target.value)} placeholder="e.g. 500mg" />
                    <input type="text" value={row.frequencies} onChange={(e) => updateRxRow(row.id, 'frequencies', e.target.value)} placeholder="OD, BD, TDS" />
                    <button type="button" className="pf-rx-del" onClick={() => removeRxRow(row.id)} disabled={acceptableRx.length <= 1}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="pf-add-row-btn" onClick={addRxRow}>
                <Plus className="w-3.5 h-3.5" /> Add Drug
              </button>
            </div>

            {/* Forbidden Rx */}
            <div className="pf-field mt-6">
              <FieldLabel text="Forbidden Rx (Contraindicated Drugs)" tooltip="Drugs the doctor absolutely MUST NOT prescribe (e.g. allergies or contraindications). Generates a critical penalty if prescribed." />
              <ChipInput
                chips={forbiddenRx}
                onChange={setForbiddenRx}
                placeholder="e.g. Metformin, Aspirin…"
                variant="danger"
              />
            </div>

            {/* Rubric */}
            <div className="pf-field mt-6">
              <FieldLabel text="Scoring Rubric" tooltip="Determines the grading weights used by the evaluator agent." />
              <select value={rubricId} onChange={(e) => setRubricId(e.target.value)}>
                {RUBRIC_PRESETS.map((r) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">
                Weights: {JSON.stringify((RUBRIC_PRESETS.find((r) => r.id === rubricId) || RUBRIC_PRESETS[0]).weights)}
              </p>
            </div>
            </div>
          </div>

        </div>
      </div>

        {/* Footer */}
        <div className="pf-panel-footer">
          <button type="button" className="pf-btn-cancel" onClick={onClose}>Cancel</button>
          {existingCase ? (
            <>
              <button type="button" className="pf-btn-cancel bg-white border-violet-200 text-violet-600 hover:bg-violet-50" onClick={() => handleSave(false)} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : <Plus className="w-4 h-4 inline mr-1" />}
                Save as New Persona
              </button>
              <button type="button" className="pf-btn-save" onClick={() => handleSave(true)} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : <Save className="w-4 h-4 inline mr-1" />}
                Save & Override Current
              </button>
            </>
          ) : (
            <button type="button" className="pf-btn-save" onClick={() => handleSave(false)} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save Patient'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
